import { useContext, useEffect, useRef, useState } from 'react'
import { useFormikContext, Field, ErrorMessage, getIn } from 'formik'
import { AnimatePresence, motion } from "framer-motion"
import classnames from 'classnames/bind'
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events'
import * as Yup from 'yup'
import { StoreContext } from '~/store'
import {
  FormContent, FormHeader, BookingSummary, ButtonDrawer, CheckboxField, RadioField,
  FieldButton, OptionsPanel, FieldDropDown, InputField, FormError, Notifications,
  AddVoucher, AddAccount, PaymentTypes, EditField, Icon, TitleInfo, CloseButton, AddButton, PaymentReference, EmptyState
}
from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { wrapWithValidation } from '~/utils'
import { getEditEstimate, confirmEdit, getBooking } from '~/lib/api'
import EditFieldsExpirationWarning from '../EditFieldsExpirationWarning'
import usePlaceBooking from '../usePlaceBooking'
import { showErrorModal } from '~/utils'
import { showContentModal, showModal } from '~/lib/modal'
import { getVoucherValue, getPaymentMethodKey } from '~/utils/data'
import { usePrevious } from '~/utils/hooks'
import * as MotionElement from '~/components/ui/MotionElement'
import * as common from '~/config/common.module.css'
import * as styles from './index.module.css'
import * as panelStyles from '~/components/ui/OptionsPanel/index.module.css'
import { useTranslation, Trans } from 'react-i18next'
import i18next from '~/i18n'
import { formatReadableDateOnly } from '../../../utils/format'
import AddMessage from '../../ui/AddMessage'

const getPaymentText = (values) => {
  if (values.payment.indexOf('Account') !== -1) {
    return values?.account?.accountName
  }
  else if (values.payment.indexOf('CreditCard') !== -1) {
    return values?.account?.maskedAccount
  }
  else if (values.payment === 'Klarna') {
    return 'Klarna'
  }
  else if (values.payment === 'ApplePay') {
    return 'Apple Pay'
  }
  return i18next.t('title-pay-in-car')
}

const renderVoucherInfo = voucher => {
  let str = i18next.t('expires-at', { date: formatReadableDateOnly(new Date(voucher.expiry)) })
  if (voucher.valueType == 'PERCENTAGE') {
    return <>{str}<br />{i18next.t('info-voucher-max', { amount: voucher.maxPriceToBeDiscounted })}</>
  }
  return str
}

const cx = classnames.bind(common)

const ConfirmBooking = ({fullscreen, currentView}) => {
  const { t } = useTranslation()
  const paymentButtonRef = useRef()
  const payInCarRef = useRef()
  const optionsButtonRef = useRef()
  const messageFieldRef = useRef()
  const nameFieldRef = useRef()
  const [isPaymentOpen, setIsPaymentOpen] = useState(false)
  const [isOptionsOpen, setIsOptionsOpen] = useState(false)
  const [isPaymentModuleOpen, setIsPaymentModuleOpen] = useState(false)
  
  const showPaymentModule = () => {
    setIsPaymentModuleOpen(true);
  }

  const hidePaymentModule = () => {
    setIsPaymentModuleOpen(false);
  }

  const { prev, next } = useContext(StepperContext)
  const { config, user, paymentAccounts, refreshUser, setCurrentPaymentAccount, getActiveBookings } = useContext(StoreContext)

  const { errors, touched, values, setFieldValue, setValues } = useFormikContext()
  const paymentError = getIn(errors, 'payment');

  // Placing a new booking (incl. credit card payment and the pending-booking poll) is
  // shared with the return trip sheet; editing an existing booking stays here.
  const { placeBooking, isLoading, setIsLoading, waitingForPoll } = usePlaceBooking({
    onNotAvailable: () => prev('how'),
    onDatetimeBeforeNow: () => prev('time'),
  })

  const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
  const disableCreditCards = !values.priceType?.paymentMethods?.includes('CreditCard')
  const disableTSABAccounts = !values.priceType?.paymentMethods?.includes('TSABAccount')
  const disableTCAAccounts = !values.priceType?.paymentMethods?.includes('TravelClearingAccount')
  const disablePayInCar = !values.priceType?.paymentMethods?.includes('PayInCar')
  const noPaymentMethodsAvailable = values.priceType !== undefined && Array.isArray(values.priceType?.paymentMethods) && values.priceType.paymentMethods.length === 0
  const selectedPaymentMethodKey = getPaymentMethodKey(values.payment, values.account)
  const disableVouchers = selectedPaymentMethodKey && values.priceType?.paymentMethodConfigurations
    ? !values.priceType.paymentMethodConfigurations.find(c => c.paymentMethod === selectedPaymentMethodKey)?.voucherEnabled
    : !values.priceType?.voucherEnabled

  const availabilityKey = values.pickupTime === '' ? 'direct' : 'prebook'
  const servicesByProvider = values.availability?.[availabilityKey]?.servicesByProvider
  const hasMultipleProviders = Array.isArray(servicesByProvider) && servicesByProvider.length > 1 &&
    !(servicesByProvider.length === 1 && servicesByProvider[0].providerData?.providerId === 'TSAB')

  const voucherDisabledMessage = (() => {
    if (!disableVouchers) return null
    const configs = values.priceType?.paymentMethodConfigurations
    if (configs?.some(c => c.voucherEnabled)) return t('info-voucher-disabled-payment-method')
    if (hasMultipleProviders) return t('info-voucher-disabled-provider')
    if (!fixedPrice) return t('info-voucher-disabled-taxameter')
    return null
  })()

  const prevAccounts = usePrevious(user?.accounts)
  const prevPaymentAccounts = usePrevious(paymentAccounts)
  const prevVouchers = usePrevious(user?.vouchers)

  const hasAccounts = user?.accounts?.length > 0
  const hasCreditCards = paymentAccounts?.length > 0
  const hasVouchers = user?.vouchers?.length > 0

  const showReference = values?.company !== false || values?.account?.type === 'TSAB' ||  values?.account?.type === 'TCA'
  // In edit mode, use isReferenceRequired from API if available
  const inEditMode = !isNaN(values.bookingId) && values.bookingId > 0
  const showPaymentEmptyState = !inEditMode && (noPaymentMethodsAvailable || (disablePayInCar && !hasCreditCards && !hasAccounts))
  const isReferenceEditable = inEditMode ? values.editableFields?.includes('invoiceReference') : true
  const needReference = inEditMode && values.editableOptions?.isReferenceRequired !== undefined
    ? values.editableOptions.isReferenceRequired
    : (values?.company?.requireReference === true ||
        (values?.account !== false && (values?.account?.type ==='TSAB' || values?.account?.type ==='TCA') && values?.account?.flags?.includes('reference')))

  const makeRequest = () => {
    const isEditMode = !isNaN(values.bookingId)

    if (isEditMode) {
      // Edit existing booking
      console.log("Edit booking request");
      setIsLoading(true)
      getEditEstimate(values.bookingId, values).then((response) => {
        console.log("Edit estimate response:", response)

        if (response.newPrice) {
          const editToken = response.editToken
          showModal(
            t('info-price-change', { newPrice: response.newPrice.finalPrice }),
            [
              { title: t('button-reject-price') },
              { title: t('button-accept-price'), callback: () => confirmEditBooking(editToken) }
            ],
            { title: t('header-price-change') }
          )
          setIsLoading(false)
        } else {
          // No price change, confirm edit directly
          confirmEditBooking(response.editToken)
        }
      })
      .catch((error) => {
        console.error("Error on edit estimate:", error)
        if (!(error?.response?.status === 401 && error?.response?.error === 'Unauthorized')) {
          showErrorModal(error?.response?.message)
        }
        setIsLoading(false)
      })
    } else {
      // Create new booking
      placeBooking()
    }
  }

  const confirmEditBooking = (editToken) => {
    setIsLoading(true)
    confirmEdit(values.bookingId, editToken)
    .then(() => {
      // Refresh store in the background while fetching fresh booking data
      getActiveBookings()
      refreshUser()
      return getBooking(values.bookingId)
    })
    .then((updatedBooking) => {
      // Patch bookingType from original booking data if the single-booking API omits it.
      // The active-bookings list endpoint includes bookingType; the single-booking endpoint
      // may not. Without it BookingDone falls back to 'Söker bil' for status 120 prebookings.
      const originalBookingType = values.booking?.bookingType
      const patchedBooking = (updatedBooking && !updatedBooking.bookingType && originalBookingType)
        ? { ...updatedBooking, bookingType: originalBookingType }
        : updatedBooking

      // Use setValues (atomic) + setTimeout like handleBookingComplete so all form values
      // are committed before BookingDone renders — prevents a race where values.booking
      // is null on the first render and effectiveBooking resolves to null.
      const currentValues = { ...values }
      setValues({ ...currentValues, booking: patchedBooking }, false)
      setIsLoading(false)
      setTimeout(() => next('done'), 0)
    })
    .catch((error) => {
      console.error("Error confirming edit:", error)
      if (!(error?.response?.status === 401 && error?.response?.error === 'Unauthorized')) {
        showErrorModal(error?.response?.message)
      }
      setIsLoading(false)
    })
  }

  const wrappedPrev = () => {
    prev('how')
  }

  const wrappedNext = wrapWithValidation(makeRequest, useFormikContext())

  const setAccount = account => {
    if (!account) {
      setFieldValue('accountReference', '', false)
    }
    setFieldValue('account', account, false)
  }

  const openOptions = () => {
    setIsOptionsOpen(true)
    setTimeout(() => {
      if (messageFieldRef.current) messageFieldRef.current.focus()
    }, 200)
  }

  const closeOptions = () => {
    setIsOptionsOpen(false)
    if (optionsButtonRef.current) optionsButtonRef.current.focus()
  }

  const openPayment = () => {
    setIsPaymentOpen(true)
    setTimeout(() => {
      if (payInCarRef.current) payInCarRef.current.focus()
    }, 200)
  }

  const closePayment = () => {
    setIsPaymentOpen(false)
    if (paymentButtonRef.current) paymentButtonRef.current.focus()
  }

  const addCard = () => {
    hidePaymentModule()
    emitCustomEvent('CASHIER_ADD_CARD', { success: refreshUser })
  }

  const openAddAccount = () => showContentModal((close) => (
    <AddAccount onComplete={() => { close(); hidePaymentModule() }} />
  ))

  const openAddVoucher = () => showContentModal((close) => (
    <AddVoucher onComplete={close} />
  ))

  const openReference = () => showContentModal((close) => (
    <EditField formValues={values} onFieldSave={(id, val) => setFieldValue(id, val, false)} fields={[{id:'accountReference', title:t('placeholder-reference')}]} title={t('placeholder-reference')} onClose={close} />
  ))

  const buttons = [
    { func: wrappedPrev, isBack: true },
    {
      func: (waitingForPoll || isLoading) ? () => {
        console.log('[ConfirmBooking] Button clicked but disabled - waitingForPoll:', waitingForPoll, 'isLoading:', isLoading)
      } : wrappedNext,
      title: inEditMode ? t('button-confirm-edit') : t('button-book-now'),
      isLoading: isLoading || waitingForPoll,
      disabled: waitingForPoll || isLoading // Disable button while waiting for poll or loading
    },
  ]

  useEffect(() => {
    if (user?.accounts?.length > prevAccounts?.length) {
      const addedAccount = user?.accounts?.find(a => {
        return (prevAccounts.find(b => b.accountNumber === a.accountNumber) === undefined) ? true : false
      })
      if (addedAccount !== undefined) {
        setFieldValue('payment', `Account-${addedAccount.accountNumber}`, false)
        setAccount(addedAccount)
      }
    }
  }, [user?.accounts])

  useEffect(() => {
    if (paymentAccounts?.length > prevPaymentAccounts?.length) {
      const addedAccount = paymentAccounts.find(a => {
        return (prevPaymentAccounts.find(b => b.accountId === a.accountId) === undefined) ? true : false
      })
      if (addedAccount !== undefined) {
        setFieldValue('payment', `CreditCard-${addedAccount.accountId}`, false)
        setAccount(addedAccount)
      }
    }
  }, [paymentAccounts])

  useEffect(() => {
    if (user?.vouchers?.length > prevVouchers?.length) {
      const addedVoucher = user?.vouchers?.find(a => {
        return (prevVouchers.find(b => b.voucherCode === a.voucherCode) === undefined) ? true : false
      })
      if (addedVoucher !== undefined) {
        if(!disableVouchers) setFieldValue('vouchers', [addedVoucher.voucherCode], false)
      }
    }
  }, [user?.vouchers])

  useEffect(() => {
    if (disableVouchers && values.vouchers?.length > 0) {
      setFieldValue('vouchers', [], false)
    }
  }, [disableVouchers])

  useEffect(() => {
    if (nameFieldRef.current) nameFieldRef.current.focus()
  }, [nameFieldRef])
  
  const contentOptions = true;

  return (
    <div className={cx(common.widgetWrapper, { isLoading, fullscreen })}>
      <FormContent>
        <FormHeader onlyFullscreen>{inEditMode ? t('edit-your-trip') : t('your-trip')}</FormHeader>
        <FormHeader onlyMobile>{inEditMode ? t('header-confirm-edit') : t('header-confirm-your-booking')}</FormHeader>
        <Notifications fullscreen={fullscreen}/>
        <BookingSummary values={values} showHow showPayment currentView={currentView} />
      </FormContent>
      <MotionElement.div className={common.contentBottom}>
        <EditFieldsExpirationWarning />
        <FormHeader onlyFullscreen>{inEditMode ? t('header-confirm-edit') : t('header-confirm-your-booking')}</FormHeader>
          
            <div className={cx('text-[18px] font-semibold tracking-[0.54px] leading-normal -mb-2 mb-2', paymentError ? 'text-[#da0a2b]' : 'text-on-surface')}>{t('title-payment')}</div>
            {inEditMode && (
              <div className={panelStyles.infoText}>
                Kan inte ändras vid redigering
              </div>
            )}
            <Field type="radio" icon="PayInCar" name="payment" title="DefaultNoSelect" value="DefaultNoSelect" hidden onClick={()=>{setAccount(false); setCurrentPaymentAccount(false, values.isCompanyTrip, 'DefaultNoSelect')}} component={RadioField} disabled={inEditMode} />
            {(!disablePayInCar || (inEditMode && values.payment === 'PayInCar')) && (
              <Field
                type="radio"
                icon="PayInCar"
                name="payment"
                title={t('title-pay-in-car')}
                value="PayInCar"
                onClick={()=>{setAccount(false); setCurrentPaymentAccount(false, values.isCompanyTrip, 'PayInCar')}}
                innerRef={payInCarRef}
                component={RadioField}
                disabled={inEditMode && values.payment !== 'PayInCar'}
              />
            )}
            {showPaymentEmptyState && (
              <EmptyState
                icon={<img src="/app/themes/taxi-stockholm/static/img/icons/icon-payments.png" alt="" className="size-8 object-contain" />}
                title={t('header-no-payment-methods')}
                description={t('info-no-payment-methods')}
              />
            )}
            {inEditMode && values.payment === 'Klarna' && (
              <Field
                type="radio"
                icon="Klarna"
                name="payment"
                title="Klarna"
                value="Klarna"
                onClick={()=>{setAccount(false); setCurrentPaymentAccount(false, values.isCompanyTrip, 'Klarna')}}
                component={RadioField}
                disabled={false}
              />
            )}
            {inEditMode && values.payment === 'ApplePay' && (
              <Field
                type="radio"
                icon="ApplePay"
                name="payment"
                title="Apple Pay"
                value="ApplePay"
                onClick={()=>{setAccount(false); setCurrentPaymentAccount(false, values.isCompanyTrip, 'ApplePay')}}
                component={RadioField}
                disabled={false}
              />
            )}
            {paymentAccounts?.map((account, index) => {
              const isSelected = values.payment === `CreditCard-${account.accountId}`
              return (
                <Field
                  key={index}
                  type="radio"
                  name="payment"
                  icon={`Card${account.cardType}`}
                  title={account.maskedAccount}
                  info={account.cardType}
                  value={`CreditCard-${account.accountId}`}
                  onClick={()=>{setAccount(account), setCurrentPaymentAccount(account, values.isCompanyTrip)}}
                  tabIndex={isPaymentOpen ? 0 : -1}
                  disabled={(inEditMode && !isSelected) || disableCreditCards}
                  disabledDescription={
                    (disableCreditCards && !fixedPrice) ? "Fastpris krävs" :
                    disableCreditCards ? "Inte tillgängligt" : null
                  }
                  component={RadioField}
                />
              )
            })}
            {inEditMode && values.account?.isOrphaned && values.account?.type === 'creditcard' && (
              <Field
                type="radio"
                icon="PayInCar"
                name="payment"
                title={values.account.maskedAccount}
                info="(Inte längre tillgängligt)"
                value={values.payment}
                onClick={()=>{setAccount(values.account), setCurrentPaymentAccount(values.account, values.isCompanyTrip)}}
                component={RadioField}
                disabled={false}
              />
            )}
            {user?.accounts?.map((account, index) => {
              const isSelected = values.payment === `Account-${account.accountNumber}`
              const disableNoDestination = account?.flags?.includes('destination') && (values?.destination==="" || values?.destination===undefined || values?.destination===null) ? true : false;
              const disableOnlyCompany = account?.restrictedToCompanyId && account?.restrictedToCompanyId !== values?.company?.id ? true : false;
              console.log("priceType", account?.flags, disableNoDestination, values?.destination);
              return (
                <Field
                  key={index}
                  type="radio"
                  name="payment"
                  icon="TSAB"
                  title={account.accountName}
                  info={account.accountNumber}
                  value={`Account-${account.accountNumber}`}
                  onClick={()=>{setAccount(account), setCurrentPaymentAccount(account, values.isCompanyTrip)}}
                  tabIndex={isPaymentOpen ? 0 : -1}
                  disabledDescription={
                    disableNoDestination ? 'Destination krävs' :
                    disableOnlyCompany ? 'Endast företagskonto' :
                    ((account.type === 'TSAB' && disableTSABAccounts)
                    || (account.type === 'TCA' && disableTCAAccounts)) ? "Inte tillgängligt" : null
                  }
                  disabled={
                    (inEditMode && !isSelected) ||
                    disableNoDestination ||
                    disableOnlyCompany ||
                    (account.type === 'TSAB' && disableTSABAccounts)
                    || (account.type === 'TCA' && disableTCAAccounts)
                  }
                  component={RadioField}
                />
            )})}
            {inEditMode && values.account?.isOrphaned && (values.account?.type === 'TSAB' || values.account?.type === 'TCA') && (
              <Field
                type="radio"
                icon="TSAB"
                name="payment"
                title={values.account.accountName}
                info={`${values.account.accountNumber} (Inte längre tillgängligt)`}
                value={values.payment}
                onClick={()=>{setAccount(values.account), setCurrentPaymentAccount(values.account, values.isCompanyTrip)}}
                component={RadioField}
                disabled={false}
              />
            )}
            {showReference && <>
              <PaymentReference
                title={t('placeholder-reference')}
                value={(!values.accountReference || values.accountReference.trim()==='') ? (needReference && touched.accountReference ? t('validation-need-reference') : '') : values.accountReference}
                onEdit={isReferenceEditable ? openReference : null}
                required
                empty={(!values.accountReference || values.accountReference.trim()==='') && needReference && !!touched.accountReference}
              />
              <div className={common.hidden}>
                <Field name="accountReference" placeholder={t('placeholder-reference')} maxLength="40" component={InputField} />
              </div>
            </>}
            {!inEditMode && (
              <div className="py-1">
                <AddButton label={t('button-add-payment')} onClick={showPaymentModule} tabIndex={isPaymentOpen ? 0 : -1} />
              </div>
            )}
            {/* 
            <div className={cx(common.fullWidth)}>
              <DefautButton isSmall onClick={openAddVoucher} tabIndex={isPaymentOpen ? 0 : -1} title={`+ ${t('button-add-new-payment')}`}/>
            </div>
            */}
            <AnimatePresence>
              {isPaymentModuleOpen && <motion.div initial={{ opacity: 0 }} exit={{ opacity: 0 }} animate={{ opacity: 1 }} className={styles.dimmer} onClick={hidePaymentModule}></motion.div>}
              {isPaymentModuleOpen && 
                <motion.div 
                  transition={{ duration: 0.15, ease: "circOut" }}
                  style={{originY: 0}} 
                  initial={{ scaleY: 0, height: 'auto' }} 
                  exit={{ scaleY: 0, height:0 }} 
                  animate={{ scaleY: 1 }} 
                  className={styles.addPaymentPanel}
                >
                  <div className={styles.addPaymentPanelTitle}>Lägg till betalmetod</div> 
                  <CloseButton onClick={hidePaymentModule} small/>
                    <div className={styles.addPaymentItem} onClick={addCard}>
                      <div className={styles.addPaymentItemTitle}>
                        <img src={`/app/themes/taxi-stockholm/static/img/option-icons/PayInCar.png`} className={styles.icon} />
                        <TitleInfo title="Betalkort" />
                      </div>
                      <Icon id="plus" />
                    </div>
                    <div className={styles.addPaymentItem} onClick={openAddAccount}>
                      <div className={styles.addPaymentItemTitle}>
                        <img src={`/app/themes/taxi-stockholm/static/img/option-icons/TSAB.png`} className={styles.icon} />
                        <TitleInfo title="Konto" />
                      </div>
                      <Icon id="plus" />
                    </div>
                </motion.div>
              }
            </AnimatePresence>
            {!inEditMode && (
              <div className={styles.vouchersPanel}>
                <div className="text-[18px] font-semibold tracking-[0.54px] leading-normal -mb-2 text-on-surface mb-2">{t('title-vouchers')}</div>
                {voucherDisabledMessage && (
                  <div className={cx(styles.instructions, common.fullWidth)}>
                    {voucherDisabledMessage}
                  </div>
                )}
                {!hasVouchers && <div className={styles.instructions}>{t('info-no-vouchers')}</div>}
                {user?.vouchers?.map((voucher, index) => (
                  <Field
                    key={index}
                    name="vouchers"
                    icon="DigitalVoucher"
                    value={voucher.voucherCode}
                    title={`${voucher.voucherCode} - ${getVoucherValue(voucher)}`}
                    info={renderVoucherInfo(voucher)}
                    disabled={disableVouchers || (values?.vouchers?.length > 0 && !values?.vouchers?.includes(voucher.voucherCode))}
                    disabledDescription={
                      disableVouchers || (values?.vouchers?.length > 0 && !values?.vouchers?.includes(voucher.voucherCode)) ? "Inte tillgängligt" : null
                    }
                    noPadding
                    tabIndex={isPaymentOpen ? 0 : -1}
                    component={CheckboxField}
                  />
                ))}
                <div className="py-1">
                  <AddButton label={t('button-add-new-voucher')} onClick={openAddVoucher} tabIndex={isPaymentOpen ? 0 : -1} />
                </div>
                
                {!disableVouchers && hasVouchers && (
                  <div className={cx(styles.instructions, common.fullWidth)} style={{ marginTop: '10px' }}>
                    <Trans
                      i18nKey="info-voucher-codes"
                      components={{ link1: <a href={config.paymentTermsLink} target="_blank"/> }}
                    />
                  </div>
                )}
              </div>
            )}
          {/* 
          <div className={panelStyles.title}>VEM</div>
          <Field name="name" placeholder={t('placeholder-name')} innerRef={nameFieldRef} component={InputField} border />
          <Field name="phone" placeholder={t('placeholder-phone')} component={InputField} border />
          <div className={cx(common.fullWidth, styles.info)}>
          <Trans 
            i18nKey="info-accept-booking-terms" 
            components={{
              link1: <a href={config.termsLink} target="_blank"/>
            }}
          />
        </div>
          
          <FieldButton title={t('title-payment')} innerRef={paymentButtonRef} onClick={openPayment} alternateBackground>
            {!inEditMode && !disableVouchers && values.vouchers.length === 0 && user?.vouchers?.length > 0 && <div className={cx(common.option)}>{t('info-num-vouchers', { count: user.vouchers.length })}</div>}
          </FieldButton>
          <FieldButton title={t('title-message-to-driver')} innerRef={optionsButtonRef} onClick={openOptions} alternateBackground />
          {showReference && <div className={common.fullWidth}>
            <Field name="accountReference" placeholder={t('placeholder-reference')} maxLength="40" component={InputField} alternateBackground />
          </div>}
          
          <FieldButton title={t('title-message-to-driver')} innerRef={optionsButtonRef} onClick={openOptions} alternateBackground />
          {values?.generalComment && <div className={cx(common.fullWidth, styles.message)}>
          <div className={common.label}>{t('title-message-to-driver')}</div>
          <div className={common.title}>{values.generalComment}</div>
        </div>}
        <div className={cx(common.fullWidth, styles.info)}>
          <Trans 
            i18nKey="info-accept-booking-terms" 
            components={{
              link1: <a href={config.termsLink} target="_blank"/>
            }}
          />
        </div>
          {isAddAccountOpen && <Modal onClose={closeAddAccount} isOpen={isAddAccountOpen}>
            <AddAccount onComplete={closeAddAccount} />
          </Modal>}
          <Modal isOpen={isOptionsOpen} onClose={closeOptions}>
            <AddMessage onClose={closeOptions} />
          </Modal>*/}
          
        
      </MotionElement.div>
      <ButtonDrawer buttons={buttons} fullscreen={fullscreen}/>

      <PaymentTypes></PaymentTypes>
    </div>
  )
}

Yup.addMethod(Yup.string, 'validPayment', function(errorMessage) {
  return this.test('test-valid-payment', errorMessage, function(value) {
    console.log('validate ppayment', value);
    const { path, createError } = this
    if(value === 'DefaultNoSelect') return createError({ path, message: errorMessage });
    return true
  })
})

ConfirmBooking.validationSchema = Yup.object({
  name: Yup.string().required(i18next.t('validation-need-name')),
  phone: Yup.string()
    .matches(/^\+?\d+$/, i18next.t('validation-only-numbers'))
    .required(i18next.t('validation-need-phone')),
  payment: Yup.string().validPayment(i18next.t('validation-need-payment')).required(i18next.t('validation-need-payment')),
  accountReference: Yup.string()
    .when(['account', 'company', 'editableOptions', 'bookingId'], (account, company, editableOptions, bookingId) => {
      const isEditMode = !isNaN(bookingId) && bookingId > 0

      // In edit mode, use isReferenceRequired from editableOptions if available
      if (isEditMode && editableOptions?.isReferenceRequired !== undefined) {
        return editableOptions.isReferenceRequired
          ? Yup.string().required(i18next.t('validation-need-reference'))
          : Yup.string()
      }

      // Normal mode: check company and account flags
      const needReference = company?.requireReference === true ||
        (account !== false && (account?.type ==='TSAB' || account?.type ==='TCA') && account?.flags.includes('reference'))
      return needReference ? Yup.string().required(i18next.t('validation-need-reference')) : Yup.string()
    }),
})

export default ConfirmBooking
